import React, { useState, useEffect } from "react";
import { t } from "../i18n";
import fetchData from "../api/fetchApi";
import InternationalPhoneField from "./forms/InternationalPhoneField";
import { isInternationalPhoneValid, phoneForApi, toE164Phone } from "../utils/phone";

// Popup component for sending message
function SendMessagePopup({ open, onClose, client, onSend }) {
  const [message, setMessage] = useState("");
  const [imageBase64, setImageBase64] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setMessage("");
      setImageBase64("");
      setSuccess("");
      setError("");
    }
  }, [open]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return setImageBase64("");
    const reader = new FileReader();
    reader.onload = (ev) => setImageBase64(ev.target.result.split(",")[1]);
    reader.readAsDataURL(file);
  };

  const handleSend = async () => {
    setSending(true);
    setSuccess("");
    setError("");
    try {
      const payload = {
        phone: [client.phone_number || client.phone],
        message,
        image_base64: imageBase64 || undefined,
      };
      await fetchData(
        `/message`,
        "POST",
        JSON.stringify(payload)
      );
      setSuccess("تم إرسال الرسالة بنجاح");
      onSend && onSend();
    } catch (err) {
      setError(err.message || "فشل إرسال الرسالة");
    } finally {
      setSending(false);
    }
  };

  if (!open || !client) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white  rounded-xl shadow-lg p-6 max-w-sm w-full relative">
        <button
          className="absolute top-2 left-2 text-gray-500 hover:text-gray-700 text-xl"
          onClick={onClose}
        >
          ×
        </button>
        <h3 className="text-lg font-bold mb-4 text-center">إرسال رسالة إلى {client.name || client.phone_number || client.phone}</h3>
        <div className="mb-3">
          <textarea
            className="w-full border rounded px-3 py-2  "
            rows={4}
            placeholder="اكتب الرسالة هنا..."
            value={message}
            onChange={e => setMessage(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="block mb-1 text-sm">إرفاق صورة (اختياري)</label>
          <input type="file" accept="image/*" onChange={handleImageChange} />
        </div>
        <div className="flex justify-end gap-2">
          <button
            className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
            onClick={onClose}
            disabled={sending}
          >
            إلغاء
          </button>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            onClick={handleSend}
            disabled={sending || !message}
          >
            {sending ? "جاري الإرسال..." : "إرسال"}
          </button>
        </div>
        {success && <div className="text-green-600 mt-2">{success}</div>}
        {error && <div className="text-red-600 mt-2">{error}</div>}
      </div>
    </div>
  );
}

export default function ClientPage() {
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [groups, setGroups] = useState([]);
  const [excelError, setExcelError] = useState("");
  const [excelFileName, setExcelFileName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [editingClientIdx, setEditingClientIdx] = useState(null);
  const [editingClientName, setEditingClientName] = useState("");
  const [editingClientPhone, setEditingClientPhone] = useState("");
  const [editingClientGroup, setEditingClientGroup] = useState("");
  const [clients, setClients] = useState([]);
  const [sendPopupOpen, setSendPopupOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Fetch groups from API on mount
  useEffect(() => {
    fetchData("user/groups")
      .then((data) => {
        setGroups(Array.isArray(data) ? data : []);
      })
      .catch(() => setGroups([]));
    // Fetch all clients
    fetchData("user/clients")
      .then((data) => {
        setClients(Array.isArray(data) ? data : []);
      })
      .catch(() => setClients([]));
  }, []);

 
 
  
  // Add state to hold upload result for table
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Upload Excel for customer list (send file to API)
  const handleCustomerExcelUpload = async (e) => {
    setExcelError("");
    setSuccess("");
    setUploadLoading(true);
    const file = e.target.files[0];
    if (!file) {
      setUploadLoading(false);
      return;
    }
    setExcelFileName(file.name);

    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      setExcelError(
        t("error") + ": " + (t("invalid-excel") || "صيغة الملف غير مدعومة")
      );
      setUploadLoading(false);
      return;
    }

    if (!selectedGroup) {
      setExcelError("يرجى اختيار المجموعة قبل رفع الملف");
      setUploadLoading(false);
      return;
    }

    // Find group_uuid by selectedGroup name
    const groupObj = groups.find(g => g.name === selectedGroup);
    const group_uuid = groupObj?.uuid;
    if (!group_uuid) {
      setExcelError("تعذر العثور على المجموعة");
      setUploadLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("group_uuid", group_uuid);

      const response = await fetchData("user/group/upload", "POST", formData);

      // Show summary and failed clients table if present
      if (response) {
        setSuccess(
          `تم رفع الملف بنجاح. 
          عدد العملاء المضافين مسبقاً: ${response.already_added_count || 0}
          عدد العملاء المضافين حديثاً: ${response.valid_count || 0}
          عدد العملاء الذين لم تتم إضافتهم: ${response.failed_count || 0}`
        );
        setExcelError("");
        setExcelFileName("");
        setUploadResult(response); // <-- store result for table below
      }
    } catch (err) {
      setExcelError(err.message || "فشل رفع الملف");
      setSuccess("");
    } finally {
      setUploadLoading(false);
    }
  };

  // Add single customer to group (API)
  const handleAddCustomer = async (e) => {
    e.preventDefault();
       console.log('f')
    if (!customerName || !customerPhone || !selectedGroup) {
      setError("يرجى إدخال الاسم ورقم الهاتف واختيار المجموعة");
      setSuccess("");
      return;
    }
    if (!isInternationalPhoneValid(customerPhone)) {
      setError("رقم الهاتف غير صالح. اختر الدولة وتحقق من الرقم.");
      setSuccess("");
      return;
    }
    setError("");
    setSuccess("");
    try {
   
      const groupObj = groups.find(g => g.name === selectedGroup);
      const group_uuid = groupObj?.uuid;
      if (!group_uuid) {
        setError("تعذر العثور على المجموعة");
        return;
      }
      await fetchData(
        "user/client",
        "POST",
        JSON.stringify({
          name: customerName,
          phone_number: phoneForApi(customerPhone),
          group_uuid: group_uuid,
        })
      );
      setSuccess(`تمت إضافة العميل (${customerName}) (${toE164Phone(customerPhone)}) إلى المجموعة (${selectedGroup})`);
      // Optionally refresh groups or clients here
      setCustomerName("");
      setCustomerPhone("");
      setSelectedGroup("");
    } catch (err) {
      setError(err.message || "فشل إضافة العميل");
    }
  };

 
  
  
 

;

  return (
    <div className="bg-white  p-6 rounded-xl shadow  mb-8">
      {/* Only the client management UI (remove group creation and group table from here) */}
      <form
        onSubmit={handleAddCustomer}
        className="bg-white  p-6 rounded-xl shadow  mb-8"
      >
        <div className="font-semibold mb-2">إضافة عميل جديد</div>
        <div className="mb-2 flex flex-col md:flex-row gap-2">
          <input
            type="text"
            className="border rounded px-3 py-2   flex-1"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="اسم العميل"
            required
          />
          <InternationalPhoneField
            id="new-customer-phone"
            className="flex-1"
            value={customerPhone}
            onChange={setCustomerPhone}
            placeholder="رقم الهاتف"
            compact
            required
          />
          <select
            className="border rounded px-3 py-2   flex-1"
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            required
          >
            <option value="">اختر المجموعة</option>
            {groups?.map((g, idx) => (
              <option key={g.uuid || idx} value={g.name}>
                {g.name}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            إضافة
          </button>
        </div>
      </form>
      {/* ...existing code for Excel upload and clients table... */}
      {/* Keep only the clients table and Excel upload here */}
      {/* ...existing code... */}
      <form className="bg-white  p-6 rounded-xl shadow  mb-8" onSubmit={e => e.preventDefault()}>
        <div className="font-semibold mb-2">رفع عملاء من ملف إكسل</div>
        <div className="mb-2 flex flex-col md:flex-row gap-2">
          <select
            className="border rounded px-3 py-2   flex-1"
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            required
          >
            <option value="">اختر المجموعة</option>
            {groups?.map((g, idx) => (
              <option key={idx} value={g.name}>
                {g.name}
              </option>
            ))}
          </select>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleCustomerExcelUpload}
            className="border rounded px-2 py-1   flex-1"
          />
          {excelFileName && (
            <span className="text-xs text-gray-500 ">
              {excelFileName}
            </span>
          )}
        </div>
        {uploadLoading && (
          <div className="text-blue-600 text-xs mb-2">جاري رفع الملف ومعالجة البيانات...</div>
        )}
        {excelError && (
          <div className="text-red-600 text-xs mb-2">{excelError}</div>
        )}
        {success && (
          <div className="text-green-600 text-xs mb-2 whitespace-pre-line">{success}</div>
        )}

        {/* Show failed clients table after upload */}
        {uploadResult && uploadResult.failed_clients && uploadResult.failed_clients.length > 0 && (
          <div className="mt-4">
            <div className="font-semibold mb-2 text-red-700">العملاء الذين لم تتم إضافتهم ({uploadResult.failed_count}):</div>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border rounded text-xs">
                <thead>
                  <tr>
                    <th className="px-2 py-1 border-b">الاسم</th>
                    <th className="px-2 py-1 border-b">رقم الهاتف</th>
                    <th className="px-2 py-1 border-b">السبب</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadResult.failed_clients.map((c, i) => (
                    <tr key={i}>
                      <td className="px-2 py-1 border-b">{c.name}</td>
                      <td className="px-2 py-1 border-b">{c.phone}</td>
                      <td className="px-2 py-1 border-b">{c.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </form>


    
      {/* Clients Table from /clients */}
      <div className="bg-white  p-6 rounded-xl shadow  mb-8">
        <h3 className="text-lg font-semibold mb-2">كل العملاء </h3>
        <div className="overflow-x-auto"></div>
        <table className="min-w-full bg-white border rounded">
          <thead>
            <tr>
              <th className="px-4 py-2 border-b text-right">الاسم</th>
              <th className="px-4 py-2 border-b text-right">رقم الهاتف</th>
              <th className="px-4 py-2 border-b text-right">المجموعة</th>
              <th className="px-4 py-2 border-b text-right">UUID</th>
              <th className="px-4 py-2 border-b text-right">تعديل</th>
              <th className="px-4 py-2 border-b text-right">إرسال رسالة</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-4 text-gray-500">
                  لا يوجد عملاء
                </td>
              </tr>
            ) : (
              clients.map((client, idx) => (
                <tr key={client.uuid || idx}>
                  <td className="px-4 py-2 border-b">
                    {editingClientIdx === `api_${idx}` ? (
                      <input
                        type="text"
                        className="border rounded px-2 py-1"
                        value={editingClientName}
                        onChange={e => setEditingClientName(e.target.value)}
                      />
                    ) : (
                      client.name || "-"
                    )}
                  </td>
                  <td className="px-4 py-2 border-b">
                    {editingClientIdx === `api_${idx}` ? (
                      <InternationalPhoneField
                        id={`edit-customer-phone-${idx}`}
                        className="min-w-[15rem]"
                        value={editingClientPhone}
                        onChange={setEditingClientPhone}
                        compact
                      />
                    ) : (
                      client.phone_number || client.phone || "-"
                    )}
                  </td>
                  <td className="px-4 py-2 border-b">
                    {editingClientIdx === `api_${idx}` ? (
                      <select
                        className="border rounded px-2 py-1"
                        value={editingClientGroup}
                        onChange={e => setEditingClientGroup(e.target.value)}
                      >
                        <option value="">اختر المجموعة</option>
                        {groups?.map((g, gIdx) => (
                          <option key={gIdx} value={g.uuid}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      client.group_name || client.group || "-"
                    )}
                  </td>
                  <td className="px-4 py-2 border-b">{client.uuid || "-"}</td>
                  <td className="px-4 py-2 border-b">
                    {editingClientIdx === `api_${idx}` ? (
                      <>
                        <button
                          className="text-green-600 font-bold mr-2"
                          type="button"
                          onClick={async () => {
                            try {
                              if (!isInternationalPhoneValid(editingClientPhone)) {
                                setError("رقم الهاتف غير صالح. اختر الدولة وتحقق من الرقم.");
                                return;
                              }
                              // Only send group_uuid if selected, else don't include it
                              const body = {
                                name: editingClientName,
                                phone_number: phoneForApi(editingClientPhone),
                              };
                              if (editingClientGroup) {
                                body.group_uuid = editingClientGroup;
                              }
                              await fetchData(
                                `user/client?uuid=${client.uuid}`,
                                "PUT",
                                JSON.stringify(body)
                              );
                              setSuccess("تم تعديل بيانات العميل بنجاح");
                              // Refresh clients after edit
                              const data = await fetchData("user/clients");
                              setClients(Array.isArray(data) ? data : []);
                              setEditingClientIdx(null);
                              setEditingClientName("");
                              setEditingClientPhone("");
                              setEditingClientGroup("");
                            } catch (err) {
                              setError(err.message || "فشل تعديل بيانات العميل");
                            }
                          }}
                        >
                          حفظ
                        </button>
                        <button
                          className="text-gray-500 font-bold"
                          type="button"
                          onClick={() => {
                            setEditingClientIdx(null);
                            setEditingClientName("");
                            setEditingClientPhone("");
                            setEditingClientGroup("");
                          }}
                        >
                          إلغاء
                        </button>
                      </>
                    ) : (
                      <button
                        className="text-blue-600 underline"
                        type="button"
                        onClick={() => {
                          setEditingClientIdx(`api_${idx}`);
                          setEditingClientName(client.name || "");
                          setEditingClientPhone(toE164Phone(client.phone_number || client.phone || ""));
                          setEditingClientGroup(client.group_uuid || "");
                        }}
                      >
                        تعديل
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-2 border-b">
                    <button
                      className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                      onClick={() => {
                        setSelectedClient(client);
                        setSendPopupOpen(true);
                      }}
                    >
                      إرسال رسالة
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <SendMessagePopup
        open={sendPopupOpen}
        onClose={() => setSendPopupOpen(false)}
        client={selectedClient}
        onSend={() => setSendPopupOpen(false)}
      />
      {(success || error) && (
        <div
          className={`mt-4 text-center ${
            success ? "text-green-600" : "text-red-600"
          }`}
        >
          {success || error}
        </div>
      )}
    </div>
  );
}
